import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ExternalLink, X } from 'lucide-react'
import { HONDA, HUSQVARNA } from '../constants/products'
import { getCarousel, type CarouselData, type Effet } from '../api/carousel'
import { getSiteLinks } from '../api/site'
import { useEscapeKey } from '../lib/useEscapeKey'

function Carousel({ photos, effet, vitesse }: { photos: string[]; effet: Effet; vitesse: number }) {
  const [idx, setIdx] = useState(0)
  useEffect(() => {
    if (photos.length < 2) return
    const t = setInterval(() => setIdx((i) => (i + 1) % photos.length), Math.max(800, vitesse))
    return () => clearInterval(t)
  }, [photos.length, vitesse])

  const frame =
    'relative mx-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-white shadow-[0_30px_70px_-25px_rgba(0,0,0,0.7)] sm:aspect-[5/4]'

  if (photos.length === 0) return null

  // Glissement horizontal : une piste qui se translate.
  if (effet === 'slide') {
    return (
      <div className={frame}>
        <div
          className="flex h-full transition-transform duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
          style={{ width: `${photos.length * 100}%`, transform: `translateX(-${idx * (100 / photos.length)}%)` }}
        >
          {photos.map((src, i) => (
            <div key={i} className="h-full shrink-0" style={{ width: `${100 / photos.length}%` }}>
              <img src={src} alt="" loading={i < 2 ? undefined : 'lazy'} decoding="async" className="h-full w-full object-contain p-6 sm:p-8" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // Morph (fondu + flou + zoom) ou Fondu simple : images empilées en absolu.
  return (
    <div className={frame}>
      {photos.map((src, i) => {
        const active = i === idx
        const inactive =
          effet === 'morph' ? 'scale-105 opacity-0 blur-lg' : 'opacity-0'
        return (
          <img
            key={i}
            src={src}
            alt=""
            loading={i < 2 ? undefined : 'lazy'}
            decoding="async"
            className={`absolute inset-0 h-full w-full object-contain p-6 transition-all duration-[900ms] ease-[cubic-bezier(0.4,0,0.2,1)] sm:p-8 ${
              active ? 'scale-100 opacity-100 blur-0' : inactive
            }`}
          />
        )
      })}
    </div>
  )
}

function BrandColumn({
  logo,
  logoClass,
  nom,
  intro,
  lien,
  marque,
  fallback,
  onVisit,
}: {
  logo: string
  logoClass: string
  nom: string
  intro: string
  lien: string
  marque: string
  fallback: string[]
  onVisit: (url: string, marque: string) => void
}) {
  const { data } = useQuery<CarouselData>({
    queryKey: ['carousel', marque],
    queryFn: () => getCarousel(marque),
  })

  const photos = data ? data.photos.map((p) => p.src) : fallback
  const effet: Effet = data?.config.effet ?? 'morph'
  const vitesse = data?.config.vitesse_ms ?? 3000

  // 4 « rangées » (logo / intro / carrousel / bouton) — alignées entre colonnes via subgrid.
  return (
    <div className="grid grid-rows-[auto_auto_auto_auto] justify-items-center gap-6 text-center lg:row-span-4 lg:grid-rows-subgrid lg:gap-7 lg:px-10">
      <div className="flex h-14 items-center justify-center">
        <img src={logo} alt={nom} className={`w-auto drop-shadow-[0_4px_16px_rgba(0,0,0,0.25)] ${logoClass}`} />
      </div>
      <p className="leading-relaxed text-white/80">{intro}</p>
      <Carousel photos={photos} effet={effet} vitesse={vitesse} />
      <div className="self-end">
        <button
          onClick={() => onVisit(lien, nom)}
          className="inline-flex items-center gap-2 rounded-full bg-orange px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition-colors hover:bg-orange-dk"
        >
          Visiter le site {nom} <ExternalLink className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export function BrandShowcase() {
  const [redirect, setRedirect] = useState<{ url: string; marque: string } | null>(null)
  useEscapeKey(() => setRedirect(null))
  const { data: links } = useQuery({ queryKey: ['site-links'], queryFn: getSiteLinks })

  return (
    <section id="marques" className="relative overflow-hidden bg-night py-16 text-white lg:py-20">
      <div className="pointer-events-none absolute -right-32 top-0 h-96 w-96 rounded-full bg-navy-lt/20 blur-3xl" />
      <div className="relative mx-auto max-w-6xl px-5 lg:px-8">
        <p className="text-center text-xs font-semibold uppercase tracking-[0.25em] text-orange">Nos marques</p>

        <div className="mt-12 grid gap-12 lg:grid-cols-2 lg:grid-rows-[auto_auto_auto_auto] lg:gap-x-0 lg:gap-y-7 lg:divide-x lg:divide-white/10">
          <BrandColumn
            logo="/img/husqvarna-logo.png"
            logoClass="h-12 sm:h-14"
            nom="Husqvarna"
            intro={HUSQVARNA.intro}
            lien={links?.lien_husqvarna ?? HUSQVARNA.lienOfficiel}
            marque="husqvarna"
            fallback={HUSQVARNA.categories.map((c) => c.img)}
            onVisit={(url, marque) => setRedirect({ url, marque })}
          />
          <BrandColumn
            logo="/img/honda-logo.png"
            logoClass="h-9 sm:h-10"
            nom="Honda"
            intro={`${HONDA.intro1} ${HONDA.intro2}`}
            lien={links?.lien_honda ?? HONDA.lienOfficiel}
            marque="honda"
            fallback={HONDA.categories.map((c) => c.img)}
            onVisit={(url, marque) => setRedirect({ url, marque })}
          />
        </div>
      </div>

      {/* Popup de redirection vers le site officiel de la marque */}
      {redirect && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-4" onClick={() => setRedirect(null)}>
          <div role="dialog" aria-modal="true" aria-label="Ouverture d'un site externe" className="w-full max-w-md rounded-2xl bg-white p-6 text-center text-ink shadow-soft" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-end">
              <button onClick={() => setRedirect(null)} className="rounded-lg p-1.5 text-ink/50 hover:bg-sand" aria-label="Fermer">
                <X className="h-5 w-5" />
              </button>
            </div>
            <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-orange/10 text-orange">
              <ExternalLink className="h-6 w-6" />
            </span>
            <h3 className="mt-4 font-display text-xl font-bold text-navy">Ouverture dans un nouvel onglet</h3>
            <p className="mt-2 text-sm leading-relaxed text-ink/70">
              Le site officiel de <strong className="text-navy">{redirect.marque}</strong> va s'ouvrir dans un nouvel onglet. Vous ne quittez pas Maison Gilles.
            </p>
            <div className="mt-6 flex justify-center gap-3">
              <button
                onClick={() => setRedirect(null)}
                className="rounded-full border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-sand"
              >
                Annuler
              </button>
              <a
                href={redirect.url}
                target="_blank"
                rel="noreferrer"
                onClick={() => setRedirect(null)}
                className="rounded-full bg-orange px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-orange-dk"
              >
                OK, ouvrir
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
